import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAppQueryClient } from "../../../lib/queryClient";
import { apiFetch } from "../../../lib/api";
import { useBlulogContext } from "./context";
import type { BlulogMinioReport } from "./context";
import { createBlulogFileUrl, fetchOverview } from "./hooks";

const PROCESS_ENDPOINT = import.meta.env.VITE_BLULOG_PROCESS_ENDPOINT || "/api/blulog/process_data";

function computeUtcTimestamp(year: number, month: number) {
  return Math.floor(Date.UTC(year, month - 1, 1, 0, 0, 0) / 1000);
}

export function BlulogRefreshPage() {
  const now = new Date();
  const queryClient = useAppQueryClient();
  const { dataSources, updateFromMinio, markDataRefreshed, setLastExtraction } = useBlulogContext();
  const { prefix, tableKey, refreshVersion } = dataSources;

  const [selectedYear, setSelectedYear] = useState(now.getUTCFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getUTCMonth() + 1);
  const [idInput, setIdInput] = useState("");
  const [enteredIds, setEnteredIds] = useState<string[]>([]);
  const [progressLog, setProgressLog] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const streamAbortRef = useRef<AbortController | null>(null);

  const tableUrl = useMemo(() => createBlulogFileUrl(prefix, tableKey), [prefix, tableKey]);

  const overviewQuery = useQuery(
    {
      queryKey: ["blulog", "overview", prefix, tableKey, refreshVersion],
      queryFn: () => fetchOverview(tableUrl),
      staleTime: 60_000,
    },
    queryClient,
  );

  const idSuggestions = useMemo(() => {
    const rows = overviewQuery.data ?? [];
    const ids = new Set<string>();
    rows.forEach((row) => {
      if (row.id1) ids.add(row.id1);
      if (row.id2) ids.add(row.id2);
    });
    return Array.from(ids).sort();
  }, [overviewQuery.data]);

  const selectedUtc = computeUtcTimestamp(selectedYear, selectedMonth);
  const selectedDateLabel = new Date(Date.UTC(selectedYear, selectedMonth - 1, 1)).toLocaleDateString(
    "fr-FR",
    {
      year: "numeric",
      month: "long",
    },
  );
  const monthLabel = new Date(Date.UTC(2000, selectedMonth - 1, 1)).toLocaleString("fr-FR", {
    month: "long",
  });

  const addLogEntry = (message: string) => {
    setProgressLog((previous) => [...previous.slice(-9), message]);
  };

  const handleAddId = (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (!trimmed) return;
    setEnteredIds((previous) => {
      if (previous.includes(trimmed)) {
        return previous;
      }
      return [...previous, trimmed];
    });
    setIdInput("");
  };

  const handleRemoveId = (id: string) => {
    setEnteredIds((previous) => previous.filter((value) => value !== id));
  };

  const adjustYear = (direction: number) => {
    setSelectedYear((previous) => previous + direction);
  };

  const adjustMonth = (direction: number) => {
    setSelectedMonth((previous) => {
      let next = previous + direction;
      let yearDelta = 0;
      while (next > 12) {
        next -= 12;
        yearDelta += 1;
      }
      while (next < 1) {
        next += 12;
        yearDelta -= 1;
      }
      if (yearDelta !== 0) {
        setSelectedYear((year) => year + yearDelta);
      }
      return next;
    });
  };

  const stopCurrentStream = () => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    setIsStreaming(false);
  };

  const handleProcessPayload = async (payload: Record<string, unknown>) => {
    if ("error" in payload) {
      const message = String(payload.error ?? "Erreur inattendue");
      addLogEntry(`Erreur: ${message}`);
      setErrorMessage(message);
      stopCurrentStream();
      return;
    }

    if ("progress" in payload && typeof payload.progress === "string") {
      addLogEntry(payload.progress);
    }

    if ("minio" in payload && payload.minio && typeof payload.minio === "object") {
      updateFromMinio(payload.minio as BlulogMinioReport);
      addLogEntry("Points MinIO mis à jour.");
    }

    if (payload.finish === 1) {
      const processedIds = Array.isArray(payload.processed_ids)
        ? (payload.processed_ids as string[])
        : [];
      addLogEntry("Traitement terminé avec succès.");
      setLastExtraction({
        processedIds,
        finishedAt: new Date(),
        message: typeof payload.progress === "string" ? payload.progress : undefined,
      });
      stopCurrentStream();
      markDataRefreshed();
      queryClient.invalidateQueries({ queryKey: ["blulog"] }).catch(() => {});
    }
  };

  const handleStartExtraction = () => {
    stopCurrentStream();
    setErrorMessage(null);
    setProgressLog([]);

    const url = new URL(PROCESS_ENDPOINT, window.location.origin);
    url.searchParams.set("utc", String(selectedUtc));
    if (enteredIds.length) {
      url.searchParams.set("ids", enteredIds.join(","));
    }

    addLogEntry(
      `Extraction initialisée (${selectedDateLabel})${enteredIds.length ? ` · ${enteredIds.length} ID(s)` : ""}`,
    );

    const controller = new AbortController();
    streamAbortRef.current = controller;
    setIsStreaming(true);

    void readProcessStream(url, controller.signal, handleProcessPayload)
      .catch((error) => {
        if (isAbortError(error)) return;
        console.error("[blulog] stream error", error);
        addLogEntry("Connexion interrompue.");
        setErrorMessage(error instanceof Error ? error.message : "Connexion interrompue avec le serveur.");
      })
      .finally(() => {
        if (streamAbortRef.current !== controller) return;
        streamAbortRef.current = null;
        setIsStreaming(false);
      });
  };

  useEffect(() => {
    return () => {
      stopCurrentStream();
    };

  }, []);

  return (
    <div className="blulog-refresh">
      <section className="blulog-refresh-block">
        <h2>Période à traiter</h2>
        <div className="blulog-yearmonth">
          <div className="blulog-yearmonth-group">
            <button type="button" onClick={() => adjustYear(-1)} disabled={isStreaming}>
              ◀
            </button>
            <span>{selectedYear}</span>
            <button type="button" onClick={() => adjustYear(1)} disabled={isStreaming}>
              ▶
            </button>
          </div>
          <div className="blulog-yearmonth-group">
            <button type="button" onClick={() => adjustMonth(-1)} disabled={isStreaming}>
              ◀
            </button>
            <span>{monthLabel}</span>
            <button type="button" onClick={() => adjustMonth(1)} disabled={isStreaming}>
              ▶
            </button>
          </div>
        </div>
        <p className="blulog-refresh-hint">Date sélectionnée : {selectedDateLabel}</p>
      </section>

      <section className="blulog-refresh-block">
        <h2>Capteurs ciblés</h2>
        <div className="blulog-id-input">
          <input
            type="text"
            list="id-suggestions"
            placeholder="Saisir un identifiant capteur"
            value={idInput}
            onChange={(event) => setIdInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAddId(idInput);
              }
            }}
            disabled={isStreaming}
          />
          <button
            type="button"
            onClick={() => handleAddId(idInput)}
            disabled={isStreaming || !idInput.trim()}
          >
            Ajouter
          </button>
          <datalist id="id-suggestions">
            {idSuggestions.map((id) => (
              <option key={id} value={id} />
            ))}
          </datalist>
        </div>

        <div className="blulog-id-list">
          {enteredIds.length ? (
            enteredIds.map((id) => (
              <span key={id} className="blulog-chip">
                {id}
                <button type="button" onClick={() => handleRemoveId(id)} disabled={isStreaming}>
                  ✕
                </button>
              </span>
            ))
          ) : (
            <p className="blulog-refresh-hint">Aucun ID ajouté. Laisser vide pour traiter tous les capteurs.</p>
          )}
        </div>
      </section>

      <section className="blulog-refresh-block">
        <h2>Actions</h2>
        <div className="blulog-actions">
          <button type="button" onClick={handleStartExtraction} disabled={isStreaming}>
            Extraction des données
          </button>
          {isStreaming ? (
            <button type="button" onClick={stopCurrentStream} className="blulog-secondary">
              Annuler
            </button>
          ) : null}
        </div>
        <p className="blulog-refresh-hint">UTC : {selectedUtc}</p>
      </section>

      <section className="blulog-refresh-block">
        <h2>Suivi</h2>
        {errorMessage ? <p className="blulog-error">{errorMessage}</p> : null}
        <ul className="blulog-log">
          {progressLog.map((message, index) => (
            <li key={`${message}-${index}`}>{message}</li>
          ))}
          {progressLog.length === 0 ? <li>En attente de lancement.</li> : null}
        </ul>
      </section>
    </div>
  );
}

async function readProcessStream(
  url: URL,
  signal: AbortSignal,
  onPayload: (payload: Record<string, unknown>) => Promise<void>,
) {
  const response = await apiFetch(url, {
    signal,
    headers: { Accept: "text/event-stream" },
  });

  if (!response.ok) {
    throw new Error(`Connexion interrompue avec le serveur (${response.status}).`);
  }

  if (!response.body) {
    throw new Error("Réponse serveur vide.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const result = await reader.read();
    if (result.done) break;

    buffer += decoder.decode(result.value, { stream: true }).replace(/\r\n/g, "\n");
    const parsed = drainSseBuffer(buffer);
    buffer = parsed.remainder;

    for (const data of parsed.events) {
      try {
        await onPayload(JSON.parse(data) as Record<string, unknown>);
      } catch (error) {
        console.error("[blulog] SSE parsing error", error);
        throw new Error("Réponse serveur invalide.");
      }
    }
  }

  const remaining = buffer.trim();
  if (!remaining) return;

  const data = readSseData(remaining);
  if (!data) return;
  await onPayload(JSON.parse(data) as Record<string, unknown>);
}

function drainSseBuffer(buffer: string) {
  const parts = buffer.split("\n\n");
  const remainder = parts.pop() ?? "";
  return {
    events: parts.map(readSseData).filter((event): event is string => Boolean(event)),
    remainder,
  };
}

function readSseData(block: string) {
  const dataLines = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart());

  if (!dataLines.length) return null;
  return dataLines.join("\n");
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}
